import { t } from './i18n.js';
import { isTouchDevice } from '../core/Engine.js';
/**
 * Scorestreak HUD — the tray, the ladder progress, the callout, and the gunship
 * overlay.
 *
 * Follows the same discipline as the rest of this HUD: every write is guarded by a
 * cached last-value, nothing in the frame path reads layout, and transitions are
 * confined to `opacity`, `transform`, `color` and `filter`. The whole widget costs
 * nothing while idle because nothing changes while idle.
 *
 * It listens only to the `streak:*` events and never touches the system directly,
 * so the system can be driven from `__streaktest` and this follows along.
 */

export const TUNING = {
  calloutHold: 2.4, // s the STREAK READY banner stays up
  calloutFade: 0.5,
  refusedHold: 1.8,
  flashHold: 0.55,
  barLerp: 9, // per second, toward the true progress ratio
};

/** 24x24 line icons, drawn rather than downloaded — no binary assets. */
const ICONS = {
  scout_pulse:
    '<circle cx="12" cy="12" r="2.4"/><path d="M12 5.2a6.8 6.8 0 0 1 6.8 6.8"/><path d="M12 1.8A10.2 10.2 0 0 1 22.2 12"/><path d="M12 18.8A6.8 6.8 0 0 1 5.2 12"/><path d="M12 22.2A10.2 10.2 0 0 1 1.8 12"/>',
  uav:
    '<path d="M12 5.5v13"/><path d="M3 11h18"/><path d="M6.5 17.5h11"/><circle cx="12" cy="4" r="1.4"/>',
  package:
    '<path d="M4 9.5h16v10H4z"/><path d="M4 9.5 7 4h10l3 5.5"/><path d="M12 4v15.5"/>',
  sentry:
    '<path d="M12 12.5 21 8"/><circle cx="9.5" cy="13.5" r="3"/><path d="M9.5 16.5 6 22"/><path d="M9.5 16.5 13 22"/><path d="M9.5 16.5v5.5"/>',
  napalm:
    '<path d="M12 22c3.3 0 5.5-2.2 5.5-5 0-3.6-3.3-5-4.2-9C11 10 9.6 11.4 9.6 13.4c0 1.4-1.1-.4-1.1-2.4-1.3 1.6-2 3.4-2 6 0 2.8 2.2 5 5.5 5Z"/>',
  helo:
    '<path d="M3 5.5h18"/><path d="M12 5.5v3"/><path d="M6 11.5h9l3.5 3H6z"/><path d="M15 13h6"/><path d="M20 11v4"/><path d="M7 17.5h8"/>',
  gunship:
    '<path d="M12 2.5 14 9h6.5l-5.2 3.9 2 6.6L12 15.6 6.7 19.5l2-6.6L3.5 9H10z"/>',
};

const TIER_LABEL = {
  recon: 'RECON',
  support: 'SUPPORT',
  denial: 'AREA DENIAL',
  power: 'AIR SUPPORT',
  ultimate: 'HEAVY ASSET',
};

export class ScorestreakHUD {
  constructor(ctx) {
    this.ctx = ctx;

    const host = ctx.hud?.root ?? document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'ss-root';
    host.appendChild(root);
    this.root = root;

    this._build(root);

    /* ---- state ---- */
    this.items = [];
    this.selected = 0;
    this.ratio = 0;
    this.ratioShown = 0;
    this.score = 0;
    this.nextName = '';
    this.nextCost = 0;
    this.prevCost = 0;
    this.calloutT = -1;
    this.flashT = -1;
    this.promptT = 0;
    this.active = new Map(); // id -> { el, name }
    /** Cached DOM writes. */
    this._w = { bar: '', label: '', nums: '', tray: '', prompt: '', hud: '' };

    this._bind();
    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');
  }

  /* -------------------------------- markup --------------------------------- */

  _build(root) {
    root.innerHTML = `
      <div class="ss-flash"></div>

      <div class="ss-callout">
        <div class="ss-callout-kicker">SCORESTREAK READY</div>
        <div class="ss-callout-name">—</div>
        <div class="ss-callout-blurb">—</div>
      </div>

      <div class="ss-ladder hud-fade">
        <div class="ss-ladder-head">
          <span class="ss-ladder-label">NEXT</span>
          <span class="ss-ladder-name">—</span>
          <span class="ss-ladder-nums">0 / 0</span>
        </div>
        <div class="ss-ladder-bar"><i></i></div>
      </div>

      <div class="ss-tray hud-fade"></div>

      <div class="ss-active hud-fade"></div>

      <div class="ss-prompt">
        <div class="ss-prompt-text">—</div>
        <div class="ss-prompt-bar"><i></i></div>
      </div>

      <div class="ss-takeover">
        <div class="ss-tk-frame">
          <i class="c tl"></i><i class="c tr"></i><i class="c bl"></i><i class="c br"></i>
        </div>
        <div class="ss-tk-reticle">
          <span class="r-h"></span><span class="r-v"></span>
          <span class="r-box"></span>
        </div>
        <div class="ss-tk-title">AC-GUNSHIP · FIRE CONTROL</div>
        <div class="ss-tk-hint">MOUSE — LAY GUNS · LMB — 25MM · RMB — 105MM</div>
        <div class="ss-tk-readout">
          <div><b data-f="clock">0</b><span>SECONDS</span></div>
          <div><b data-f="rapid">0</b><span>25MM</span></div>
          <div><b data-f="shells">0</b><span>105MM</span></div>
        </div>
      </div>`;

    this.flashEl = root.querySelector('.ss-flash');
    this.calloutEl = root.querySelector('.ss-callout');
    this.calloutName = root.querySelector('.ss-callout-name');
    this.calloutBlurb = root.querySelector('.ss-callout-blurb');
    this.calloutKicker = root.querySelector('.ss-callout-kicker');

    this.ladderEl = root.querySelector('.ss-ladder');
    this.ladderName = root.querySelector('.ss-ladder-name');
    this.ladderNums = root.querySelector('.ss-ladder-nums');
    this.ladderFill = root.querySelector('.ss-ladder-bar i');

    this.trayEl = root.querySelector('.ss-tray');
    /**
     * Tap a slot to deploy that streak. `pointerup` rather than `click` for the
     * same reason the command bar uses it: the 300 ms tap delay is the difference
     * between a control that feels native and one that feels like a web page.
     *
     * The request goes on the bus rather than calling the system directly — these
     * two halves have only ever spoken `streak:*` to each other and this is not the
     * place to start making an exception.
     */
    this.trayEl.addEventListener('pointerup', (e) => {
      const slot = e.target.closest?.('.ss-slot');
      if (!slot) return;
      e.preventDefault();
      e.stopPropagation();
      const index = [...this.trayEl.querySelectorAll('.ss-slot')].indexOf(slot);
      if (index >= 0) ctx.events.emit('streak:request', { index });
    });
    this.activeEl = root.querySelector('.ss-active');

    this.promptEl = root.querySelector('.ss-prompt');
    this.promptText = root.querySelector('.ss-prompt-text');
    this.promptFill = root.querySelector('.ss-prompt-bar i');

    this.tkEl = root.querySelector('.ss-takeover');
    this.tkClock = root.querySelector('[data-f="clock"]');
    this.tkRapid = root.querySelector('[data-f="rapid"]');
    this.tkShells = root.querySelector('[data-f="shells"]');
  }

  /* ------------------------------ event wiring ----------------------------- */

  _bind() {
    const ev = this.ctx.events;
    this._offs = [];
    const on = (n, f) => this._offs.push(ev.on(n, f));

    on('streak:progress', (p) => {
      if (!p) return;
      this.score = p.score;
      this.ratio = p.ratio ?? 0;
      this.nextName = p.next?.name ?? 'MAX';
      this.nextCost = p.next?.cost ?? 0;
      this.prevCost = p.prev ?? 0;
    });

    on('streak:tray', (p) => {
      if (!p) return;
      this.items = p.items ?? [];
      this.selected = p.selected ?? 0;
      this._renderTray();
    });

    on('streak:earned', (p) => {
      if (!p) return;
      this.calloutKicker.textContent =
        p.source === 'package' ? 'CARE PACKAGE' : 'SCORESTREAK READY';
      this.calloutName.textContent = p.name ?? '';
      this.calloutBlurb.textContent = `${TIER_LABEL[p.tier] ?? ''} · ${p.blurb ?? ''}`;
      this.calloutEl.classList.toggle('is-ultimate', p.tier === 'ultimate');
      this.calloutT = 0;
      this.flashT = 0;
      this.flashEl.classList.toggle('is-ultimate', p.tier === 'ultimate');
    });

    on('streak:refused', (p) => {
      this.calloutKicker.textContent = t('ss.trayFull');
      this.calloutName.textContent = p?.name ?? '';
      this.calloutBlurb.textContent = t('ss.deployFirst');
      this.calloutEl.classList.remove('is-ultimate');
      this.calloutT = TUNING.calloutHold - TUNING.refusedHold;
    });

    on('streak:lost', (p) => {
      if (!p?.count) return;
      this.calloutKicker.textContent = t('ss.streaksLost');
      this.calloutName.textContent = t('ss.undeployed', { n: p.count });
      this.calloutBlurb.textContent = t('ss.killedInAction');
      this.calloutEl.classList.remove('is-ultimate');
      this.calloutT = TUNING.calloutHold - TUNING.refusedHold;
    });

    on('streak:deploy', (p) => {
      if (!p) return;
      this._addActive(p.id, p.name);
      this.calloutT = TUNING.calloutHold; // clear any pending callout instantly
    });

    on('streak:end', (p) => this._removeActive(p?.id));

    on('streak:prompt', (p) => {
      this.promptTextValue = p?.text ?? null;
      this.promptProgress = p?.progress ?? 0;
    });

    on('streak:takeover', (p) => {
      this.tkEl.classList.toggle('is-on', !!p?.active);
      this.root.classList.toggle('is-takeover', !!p?.active);
    });

    on('streak:hud', (p) => {
      this.tkData = p;
    });
  }

  /* ------------------------------- rendering ------------------------------- */

  _renderTray() {
    // The tray changes only when a streak is earned or spent, so a full rebuild
    // here is cheaper than diffing — and it never happens inside a firefight frame.
    const key = this.items.map((i) => i.id).join(',') + '|' + this.selected;
    if (key === this._w.tray) return;
    this._w.tray = key;

    if (!this.items.length) {
      this.trayEl.textContent = '';
      this.trayEl.classList.remove('is-armed');
      return;
    }
    this.trayEl.classList.add('is-armed');
    let html = '';
    for (let i = 0; i < this.items.length; i++) {
      const it = this.items[i];
      const sel = i === this.selected ? ' is-selected' : '';
      html +=
        `<div class="ss-slot${sel}" data-tier="${it.tier}">` +
        `<svg class="ss-ico" viewBox="0 0 24 24">${ICONS[it.id] ?? ''}</svg>` +
        `<div class="ss-slot-name">${it.name}</div>` +
        `<div class="ss-slot-key">${i + 1}</div>` +
        '</div>';
    }
    /**
     * The hint names keys on a keyboard and the gesture on a touchscreen, because
     * "4 CYCLE · 5 DEPLOY" on a phone is not an instruction, it is a puzzle — and
     * those keys were the *only* way to use a streak, so everything the player
     * earned on a phone was unreachable.
     */
    html +=
      '<div class="ss-tray-hint">' +
      (isTouchDevice()
        ? t('ss.tapDeploy')
        : (this.items.length > 1 ? `<b>4</b> ${t('ss.cycle')} · ` : '') + `<b>5</b> ${t('ss.deploy')}`) +
      '</div>';
    this.trayEl.innerHTML = html;
  }

  _addActive(id, name) {
    if (this.active.has(id)) return;
    const el = document.createElement('div');
    el.className = 'ss-chip';
    el.innerHTML =
      `<svg class="ss-ico" viewBox="0 0 24 24">${ICONS[id] ?? ''}</svg>` +
      `<span class="ss-chip-name">${name ?? id}</span>`;
    this.activeEl.appendChild(el);
    this.active.set(id, { el, name });
  }

  _removeActive(id) {
    const rec = this.active.get(id);
    if (!rec) return;
    rec.el.classList.add('is-gone');
    const el = rec.el;
    setTimeout(() => el.remove(), 260);
    this.active.delete(id);
  }

  /* --------------------------------- frame --------------------------------- */

  update(dt) {
    /* ---- ladder ---- */
    this.ratioShown += (this.ratio - this.ratioShown) * Math.min(1, TUNING.barLerp * dt);
    const pct = `${(this.ratioShown * 100).toFixed(1)}%`;
    if (pct !== this._w.bar) {
      this.ladderFill.style.width = pct;
      this._w.bar = pct;
    }
    if (this.nextName !== this._w.label) {
      this.ladderName.textContent = this.nextName;
      this._w.label = this.nextName;
    }
    const nums = this.nextCost ? `${this.score} / ${this.nextCost}` : `${this.score}`;
    if (nums !== this._w.nums) {
      this.ladderNums.textContent = nums;
      this._w.nums = nums;
    }

    /* ---- callout ---- */
    if (this.calloutT >= 0) {
      this.calloutT += dt;
      const hold = TUNING.calloutHold;
      const fade = TUNING.calloutFade;
      let o = 1;
      if (this.calloutT < 0.14) o = this.calloutT / 0.14;
      else if (this.calloutT > hold) o = Math.max(0, 1 - (this.calloutT - hold) / fade);
      this.calloutEl.style.opacity = o.toFixed(3);
      // Slight settle rather than a static block: it has to feel like an arrival.
      const s = 1 + Math.max(0, 0.1 - this.calloutT) * 1.4;
      this.calloutEl.style.transform = `translate(-50%,0) scale(${s.toFixed(3)})`;
      if (this.calloutT > hold + fade) {
        this.calloutT = -1;
        this.calloutEl.style.opacity = '0';
      }
    }

    /* ---- screen flash ---- */
    if (this.flashT >= 0) {
      this.flashT += dt;
      const k = 1 - this.flashT / TUNING.flashHold;
      this.flashEl.style.opacity = k > 0 ? (k * k).toFixed(3) : '0';
      if (k <= 0) this.flashT = -1;
    }

    /* ---- contextual prompt ---- */
    const want = this.promptTextValue ?? null;
    if (want !== this._w.prompt) {
      this._w.prompt = want;
      this.promptEl.classList.toggle('is-on', !!want);
      if (want) this.promptText.textContent = want;
    }
    if (want) {
      const p = `${((this.promptProgress ?? 0) * 100).toFixed(0)}%`;
      if (p !== this._w.promptBar) {
        this.promptFill.style.width = p;
        this._w.promptBar = p;
      }
    }

    /* ---- takeover readout ---- */
    const d = this.tkData;
    if (d) {
      const key = `${d.left}|${d.rapid}|${d.shells}`;
      if (key !== this._w.hud) {
        this._w.hud = key;
        this.tkClock.textContent = d.left;
        this.tkRapid.textContent = d.rapid;
        this.tkShells.textContent = d.shells;
      }
    }
  }

  dispose() {
    this._unregister?.();
    for (const off of this._offs ?? []) off();
    this.root.remove();
  }
}
